<template>
	<section
		class="office-hours-section new-settings__section"
		aria-labelledby="office-hours-section-title"
	>
		<header class="new-settings__section-header">
			<div class="new-settings__section-header-row">
				<div class="new-settings__section-header-text">
					<h1
						id="office-hours-section-title"
						class="new-settings__section-title"
					>
						Office Hours
					</h1>
					<p class="new-settings__section-subtitle">
						Manage Office Opening Hours and Weekly Schedule
					</p>
				</div>
				<div
					v-if="!isFetchingOfficeHours"
					class="btn-pill-group new-settings__section-actions"
				>
					<template v-if="isEditing">
						<button
							type="button"
							class="btn-pill btn-pill--sm"
							:disabled="busy"
							@click="cancelEditing"
						>
							Cancel
						</button>
						<button
							type="button"
							class="btn-pill btn-pill--sm"
							:disabled="busy || disableSavebtn"
							@click="onSaveClick"
						>
							Save
						</button>
					</template>
					<button
						v-else
						type="button"
						class="btn-pill btn-pill--sm"
						@click="startEditing"
					>
						Edit
					</button>
				</div>
			</div>
		</header>

		<div class="new-settings__card office-hours-section__table-card">
			<div class="office-hours-section__table-body">
				<engage-loader v-if="isFetchingOfficeHours" overlay />
				<div class="office-hours-section__table-scroll">
					<table class="office-hours-section__table">
						<colgroup>
							<col class="office-hours-section__col-day" />
							<col class="office-hours-section__col-toggle" />
							<col
								span="4"
								class="office-hours-section__col-time"
							/>
						</colgroup>
						<thead>
							<tr class="office-hours-section__table-group-row">
								<th
									scope="col"
									class="office-hours-section__table-group-spacer"
									aria-hidden="true"
								/>
								<th
									scope="col"
									class="office-hours-section__table-group-spacer"
									aria-hidden="true"
								/>
								<th colspan="2" scope="colgroup">
									OFFICE HOURS
								</th>
								<th colspan="2" scope="colgroup">
									LUNCH HOURS
								</th>
							</tr>
							<tr class="office-hours-section__table-subhead-row">
								<th
									scope="col"
									class="office-hours-section__table-subhead-day"
								>
									DAY
								</th>
								<th
									scope="col"
									class="office-hours-section__table-subhead-toggle"
								>
									OPEN/CLOSED
								</th>
								<th scope="col">START</th>
								<th scope="col">END</th>
								<th scope="col">START</th>
								<th scope="col">END</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(day, index) in officeHours"
								:key="day.day_name"
							>
								<th
									scope="row"
									class="office-hours-section__day"
								>
									{{ day.day_name }}
								</th>
								<td class="office-hours-section__toggle-cell">
									<label class="office-hours-section__switch">
										<input
											type="checkbox"
											:checked="day.is_open"
											:disabled="!isEditing"
											@change="
												updateOpenStatus(
													index,
													$event.target.checked
												)
											"
										/>
										<span
											class="office-hours-section__slider"
											:class="{
												close: !day.is_open,
											}"
										>
											{{
												day.is_open ? 'Open' : 'Closed'
											}}
										</span>
									</label>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(
												day,
												'starts_at'
											) === 'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(
												day,
												'starts_at'
											) === 'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.starts_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'starts_at',
													$event.target.value
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(day, 'ends_at') ===
												'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(day, 'ends_at') ===
												'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.ends_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'ends_at',
													$event.target.value
												)
											"
											@blur="
												onTimeBlur(
													day.day_name,
													'ends_at',
													day.ends_at
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(
												day,
												'lunch_starts_at'
											) === 'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(
												day,
												'lunch_starts_at'
											) === 'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.lunch_starts_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'lunch_starts_at',
													$event.target.value
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(
												day,
												'lunch_ends_at'
											) === 'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(
												day,
												'lunch_ends_at'
											) === 'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.lunch_ends_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'lunch_ends_at',
													$event.target.value
												)
											"
											@blur="
												onTimeBlur(
													day.day_name,
													'lunch_ends_at',
													day.lunch_ends_at
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div
					v-if="isEditing && disableSavebtn && !isFetchingOfficeHours"
					class="office-hours-section__error"
				>
					{{ errorMessage }}
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import officeHoursSchedule from '@/mixins/officeHoursSchedule'
import messages from '@/config/messages'
import util from '@/util'
import EngageLoader from '@/components/EngageLoader.vue'

/**
 * Office hours settings section with inline schedule editing.
 */
export default {
	components: {
		EngageLoader,
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		/**
		 * Reset local edit state and schedule rows before loading another partner.
		 *
		 * @return {void}
		 */
		resetOfficeHoursSection() {
			this.isEditing = false
			this.busy = false
			this.clearOfficeHours()
			this.extracted = []
		},

		/**
		 * Fetch office hours and partner IVR flags.
		 *
		 * @return {Promise<void>}
		 */
		async loadOfficeHours() {
			if (!this.partner?.id) {
				return
			}

			try {
				this.isFetchingOfficeHours = true
				await this.fetchOfficeHours()
				await this.fetchPartner(this.partner.id)
			} catch (e) {
				this.$alert.error(messages.defaultErrorMessage)
			} finally {
				this.isFetchingOfficeHours = false
			}
		},

		/**
		 * Enter edit mode.
		 *
		 * @return {void}
		 */
		startEditing() {
			this.isEditing = true
		},

		/**
		 * Discard unsaved changes and exit edit mode.
		 *
		 * @return {void}
		 */
		cancelEditing() {
			this.restoreFromExtracted()
			this.isEditing = false
		},

		/**
		 * Restore office hours from the last saved snapshot.
		 *
		 * @return {void}
		 */
		restoreFromExtracted() {
			this.officeHours = this.extracted.map(item => ({
				day_name: util.capitalizeFirstLetter(item.day_name),
				starts_at: item.starts_at,
				ends_at: item.ends_at,
				lunch_starts_at: item.lunch_starts_at,
				lunch_ends_at: item.lunch_ends_at,
				is_open: item.is_open,
			}))
		},

		/**
		 * Save office hours and exit edit mode on success.
		 *
		 * @return {Promise<void>}
		 */
		async onSaveClick() {
			if (this.disableSavebtn) {
				return
			}

			const saved = await this.officeHoursSubmit()
			if (saved) {
				this.isEditing = false
			}
		},

		cancelAllEditing() {
			if (this.isEditing) {
				this.cancelEditing()
			}
		},
	},

	mixins: [officeHoursSchedule],
	name: 'OfficeHoursSection',

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		/**
		 * Reload schedule when the active partner changes (e.g. route partner switch).
		 *
		 * @param {Number} newId
		 * @param {Number|undefined} oldId
		 * @return {Promise<void>}
		 */
		async 'partner.id'(newId, oldId) {
			if (!newId || newId === oldId) {
				return
			}

			if (oldId != null) {
				this.resetOfficeHoursSection()
			}

			await this.loadOfficeHours()
		},
	},

	/**
	 * Load schedule data when the section is shown.
	 *
	 * @return {Promise<void>}
	 */
	async mounted() {
		await this.loadOfficeHours()
	},

	beforeDestroy() {
		this.cancelAllEditing()
	},

	deactivated() {
		this.cancelAllEditing()
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			isEditing: false,
		}
	},
}
</script>
